import { SectionCard } from '@/components/SectionCard/SectionCard'
import type { LicenseResponseBody } from '@/features/Licenses/api/licenseHooks'
import type { LicenseActionId } from '@/features/Licenses/lib/deriveActions'
import { isSelfServiceLicense } from '@/features/Licenses/lib/licensePredicates'
import { Box, Button, DataList, Tooltip } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const NUMBER_FORMAT = new Intl.NumberFormat('en-US')

type Props = {
  license: LicenseResponseBody
  onAction: (id: LicenseActionId) => void
}

export function EntitlementsPanel({ license, onAction }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const isActive = license.status === 'ACTIVE'
  const isSelfService = isSelfServiceLicense(license)
  const isDisabled = !isActive || isSelfService

  let disabledReason: string | undefined
  if (isSelfService) {
    disabledReason = String(
      t('licenses.actions.disabledReason.entitlementsSelfService'),
    )
  } else if (!isActive) {
    disabledReason = String(
      t('licenses.actions.disabledReason.entitlementsNotActive'),
    )
  }

  const handleEdit = () => {
    onAction('editEntitlements')
  }

  const editButton = (
    <Button
      variant="outline"
      size="sm"
      disabled={isDisabled}
      onClick={handleEdit}
    >
      {String(t('licenses.detail.entitlements.editButton'))}
    </Button>
  )

  let editButtonOrTooltip: ReactNode
  if (!isDisabled) {
    editButtonOrTooltip = editButton
  } else {
    editButtonOrTooltip = (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <Box>{editButton}</Box>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>
            <Tooltip.Arrow />
            {disabledReason}
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
    )
  }

  return (
    <SectionCard
      h="100%"
      title={String(t('licenses.detail.entitlements.title'))}
      actions={editButtonOrTooltip}
    >
      {/* Same `DataList` as the other cards on the page. No copy affordance:
          these are quantities you read off, not identifiers you paste. */}
      <DataList.Root orientation="vertical" gap={4}>
        <DataList.Item>
          <DataList.ItemLabel>
            {String(t('licenses.detail.entitlements.maxConnections'))}
          </DataList.ItemLabel>
          <DataList.ItemValue fontSize="md" fontWeight="semibold">
            {NUMBER_FORMAT.format(license.entitlements.maxConnections)}
          </DataList.ItemValue>
        </DataList.Item>
        <DataList.Item>
          <DataList.ItemLabel>
            {String(t('licenses.detail.entitlements.maxMessagesPerSecond'))}
          </DataList.ItemLabel>
          <DataList.ItemValue fontSize="md" fontWeight="semibold">
            {`${NUMBER_FORMAT.format(license.entitlements.maxMessagesPerSecond)} msg/s`}
          </DataList.ItemValue>
        </DataList.Item>
      </DataList.Root>
    </SectionCard>
  )
}
